<template src="./template.html"/>
<script>
import { waitValue } from '@/utils/promises'
import models from './models'
import mixinFilters from './Filters'
import mixinsSwapRank from './mixinsSwapRank'
import ExcelExporter from '@/components/ExcelExporter'
import JsonExporter from '@/components/JsonExporter'
import { api } from '@/services/api'

export default {
  name: 'Grid',
  components: {
    'grid-toolbar': () => import(/* webpackChunkName: 'Grid' */ './GridToolbar'),
    'grid-tool-controls': () => import(/* webpackChunkName: 'Grid' */ './GridToolControls'),
    'dialog-delete': () => import(/* webpackChunkName: 'Dialogs' */ '@/components/Dialogs/DialogDelete'),
    'grid-row': () => import(/* webpackChunkName: 'Grid' */ './GridRow'),
    'grid-column': () => import(/* webpackChunkName: 'Grid' */ './Columns/GridColumn'),
    'grid-column-controls': () => import(/* webpackChunkName: 'Grid' */ './ColumnControls/Index')
  },
  mixins: [mixinFilters, mixinsSwapRank],
  props: {
    config: { type: Object },
    prepareGet: { type: Function, default: (data) => data },
    addParamsToGetRequest: { type: Function, default: () => ({}) },
    getRequestMiddleware: { type: Function, default: (getCallback) => (getCallback()) }
  },
  data () {
    return {
      // flags
      isVisible: false,
      isInit: false,
      isLoading: false,
      // main
      model: null,
      data: [],
      clonedHelper: null,
      newHelper: null,
      current: 1,
      // helpers
      scroll: { y: 0, x: 0, '-x': 0 },
      // Private
      privateFilters: ''
    }
  },
  watch: {
    '$route': 'handleRoute',
    'config': 'parseModel',
    'scroll': 'handleChangeScroll',
    isLoading (value) {
      this.$emit('onChangeLoading', value)
    }
  },
  computed: {
    permissions () {
      return this.$store.getters.get('permissions')
    },
    querySelected () {
      if (Array.isArray(this.model.store.get.selected)) {
        return this.model.store.get.selected.join(',')
      } else {
        return this.checkJsTemplate(this.model.store.get.selected)
      }
    }
  },
  created () {
    this.requestGetDataDebounce = this.$_.debounce(this.requestGetData, 75)
  },
  mounted () {
    this.parseModel()

    this.$bus.$on('Refresh-Grid', this.handlerBusRefreshGrid)
    // bus methods
    this.$bus.$on(`Call-Grid-Create-${this.model.id}`, this.requestCreateData)
    this.$bus.$on(`Call-Grid-Patch-${this.model.id}`, this.requestPatchData)
    this.$bus.$on(`Call-Grid-Delete-${this.model.id}`, this.handlerDelete)
    this.$bus.$on(`Filters-Grid-Init-${this.model.id}`, this.handleFiltersInit)
  },
  beforeDestroy () {
    this.$bus.$off('Refresh-Grid', this.handlerBusRefreshGrid)
    // bus methods
    this.$bus.$off(`Call-Grid-Create-${this.model.id}`, this.requestCreateData)
    this.$bus.$off(`Call-Grid-Patch-${this.model.id}`, this.requestPatchData)
    this.$bus.$off(`Call-Grid-Delete-${this.model.id}`, this.handlerDelete)
    this.$bus.$off(`Filters-Grid-Init-${this.model.id}`, this.handleFiltersInit)
  },
  methods: {
    parseModel () {
      // reset helpers
      this.resetHelpers().resetData()
      // parse
      let model = this.$_.merge(models.grid(this), this.config)
      // header
      if (!model.header.title.value) model.header.title.value = this.L10n(this.$route.meta.label) || ''
      model.header.title.value = this.checkEs6AndRun(model.header.title.value)
      // toolbar
      model.header.controls = this.parseToolControls(model.header.controls)
      model.toolbar.filters = model.toolbar.filters
        .filter(filter => this.permission(this.permissions, filter.permission))
        .map(filter => this.$_.merge(models.gridColumnFilter(), filter))
      // controls
      this.$_.each(model.rowControls, this.parseControls)
      // columns
      this.parseColumns(model)
      // apply model
      this.model = this.parseJsTemplate(model)
      // TODO: Registry Filters
      this.registryFilters()
    },
    parseColumns (Model) {
      Model.columns = Model.columns
      // check column permission
        .filter(column => this.permission(this.permissions, column.permission))
        .map((item, index) => {
          let column = this.$_.merge(models.gridColumn(), item)
          // fixed
          if (column.isFixed) {
            column.classes += ` ${column.td.classes} right-fixed-shadow`
            if (
              (!Model.rowControls.left.controls.length || !Model.rowControls.left.isFixed) ||
              (index && !Model.columns[index - 1].isFixed)
            ) {
              column.td.classes += ` ${column.td.classes} left-fixed-shadow`
            }
          }
          // templates
          switch (column.template) {
            case 'boolean': {
              column.classes += ' text-center'
              column.th.styles = Object.assign({ width: '11rem' }, column.th.styles)
              column.export.excel.type = 'b'
              break
            }
            case 'date': {
              column.td.classes += ' text-nowrap'
              column.th.styles = Object.assign({ width: '15rem' }, column.th.styles)
              column.export.excel.type = 'd'
              break
            }
            case 'email': {
              column.td.classes += ' text-nowrap'
              break
            }
            case 'number': {
              column.classes += ' text-center'
              break
            }
            case 'rank': {
              column.classes += ' ranked'
              break
            }
          }
          // filters
          if (column.filter) {
            column.filter = this.$_.merge(models.gridColumnFilter(), column.filter)
          }
          // editors
          if (column.editor) {
            // check editor column permission
            if (this.permission(this.permissions, Model.permission.edit) && this.permission(this.permissions, column.editor.permission)) {
              // validate rules
              if (
                typeof column.editor.required === 'string' &&
                !['true', 'false'].includes(column.editor.required)
              ) {
                column.editor.required = this.getValidationRule(column.editor.required)
              }
              // field
              if (!column.editor.field) column.editor.field = column.field
            } else {
              column.editor = null
            }
          }
          // export
          // -excel
          if (column.export.excel.isExport) {
            if (!column.export.excel.field) column.export.excel.field = column.field
            if (!column.export.excel.title) column.export.excel.title = column.title
          }
          if (column.export.json.isExport) {
            if (!column.export.json.field) column.export.json.field = column.field
          }
          column.td.classes = this.$_.uniq(`${column.td.classes} ${column.classes}`.split(' '))
          column.th.classes = this.$_.uniq(`${column.th.classes} ${column.classes}`.split(' '))
          return column
        })
    },
    parseControls (Align) {
      Align.controls = Align.controls
        .filter(control => this.permission(this.permissions, control.permission))
        .map(item => {
          let control = null
          if (typeof item === 'string') {
            control = models.gridControls(item, this)
          } else if (item.type) {
            control = this.$_.merge(models.gridControls(item.type, this), item)
          }
          return control
        })
    },
    parseToolControls (controls) {
      let cloneControls = this.$_.cloneDeep(controls)
      this.$_.each(cloneControls, control => {
        if (typeof control === 'object' && control.isVisible) {
          control.isVisible = this.permission(this.permissions, control.permission)
        }
      })
      return cloneControls
    },
    createGetRequest (AllPages = false) {
      let requestProps = {
        url: this.checkJsTemplate(this.model.store.get.url),
        params: { 'count': true }
      }
      // SELECTED
      let selected = this.querySelected
      if (selected) requestProps.params['select'] = selected

      // PAGINATION
      if (this.model.pagination.size !== 'all' && !AllPages) {
        if (this.model.pagination.current - 1) {
          requestProps.params['skip'] = (this.model.pagination.current - 1) * this.model.pagination.size
        }
        requestProps.params['take'] = this.model.pagination.size
      }
      // SORTABLE
      Object.assign(requestProps.params, this.createSortableParams(requestProps))

      // FILTERS
      let filters = []
      // main filter
      let mainFilters = this.checkJsTemplate(this.model.store.get.filters)
      if (mainFilters) filters.push(mainFilters)
      // private filters
      if (this.privateFilters) filters.push(this.privateFilters)
      // filters
      filters = [...this.createFiltersParams(), ...filters]
      // create filter param
      filters = this.$_.sortedUniq(filters.filter(filter => filter))
      if (filters.length) requestProps.params['filter'] = filters.join(' && ')

      // EXPAND
      let expand = this.checkJsTemplate(this.model.store.get.expand)
      if (expand) requestProps.params['expand'] = expand

      const moreParams = this.addParamsToGetRequest()

      if (moreParams && typeof 'object') {
        requestProps.params = {
          ...requestProps.params,
          ...moreParams
        }
      } else {
        console.error(`The function: ${this.addParamsToGetRequest} must return an object`)
      }

      // return completed
      return requestProps
    },
    createSortableParams () {
      let storeOrderBy = this.model.store.get.orderBy
      // Sortable by columns
      if (this.model.sortable.field && this.model.sortable.order) {
        let column = this.$_.find(this.model.columns, { field: this.model.sortable.field })
        if (column) {
          // Sortable column
          if (column.sortable.isAvailable) {
            if (typeof column.sortable.custom === 'string') {
              return { 'orderBy': `${column.sortable.custom} ${this.model.sortable.order}` }
            } else if (column.sortable.custom[this.model.sortable.order]) {
              return { 'orderBy': this.checkJsTemplate(column.sortable.custom[this.model.sortable.order]) }
            } else {
              return { 'orderBy': `${this.model.sortable.field} ${this.model.sortable.order}` }
            }
          } else {
            // eslint-disable-next-line
            console.warn('Is not available sorted column in configuration', column)
          }
        } else {
          // eslint-disable-next-line
          console.warn('Error createSortableParams, not found column', this.model.sortable.field)
        }
      } else if (storeOrderBy.field) {
        return { 'orderBy': `${storeOrderBy.field} ${storeOrderBy.order}` }
      }
      return {}
    },
    createFiltersParams () {
      return this.$_.reduce(this.filtersQuery, (acc, filter) => {
        if (filter.value) acc.push(filter.value)
        return acc
      }, [])
    },
    // REQUESTS,
    requestGetData () {
      this.getRequestMiddleware(this._requestGetData)
    },
    _requestGetData () {
      this.isLoading = true
      // create request
      let requestProps = this.createGetRequest()
      // send
      return api
        .get(requestProps.url, {
          requestId: `requestGetData-${this._uid}`,
          params: requestProps.params
        })
        .then(response => {
          let data = null
          if (this.model.store.get.parser) {
            data = this.model.store.get.parser(response.data.value)
          } else {
            data = response.data.value
          }
          data = this.prepareGet(data)
          let currentPage = this.model.pagination.current
          let lastIndex = Math.min(data.length - 1, this.model.pagination.size - 1)
          this.data = data.map((item, index) => {
            return Object.assign({
              ranked_: {
                isFirst: index === 0 && currentPage === 1,
                isLast: index === lastIndex && currentPage === Math.ceil(response.data.count / this.model.pagination.size)
              },
              status_: {
                isNew: (this.newHelper && this.newHelper === item[this.model.store.mainField]),
                isClone: (this.clonedHelper && this.clonedHelper === item[this.model.store.mainField]),
                isFirst: index === 0,
                isLast: index === lastIndex
              }
            }, item)
          })
          // update total
          this.model.pagination.total = response.data.count
          this.clonedHelper = this.newHelper = null
          // emit loaded event
          this.$bus.$emit('handlerLoaded-Grid', { grid: this.model.id, data: response.data })
          this.$emit('onGet', response.data.value, response.data.count)
          this.isLoading = false
          this.$nextTick(() => {
            this.resetScroll()
          })
        })
        .catch(e => {
          this.isLoading = false
          this.requestErrorHandler(e, 'Error load data')
        })
    },
    requestCloneData (data, Callback) {
      this.isLoading = true
      let url = this.checkJsTemplate(this.model.store.clone.url, data)
      return api
        .get(url)
        .then(response => {
          if (Callback) Callback(response)
          this.APIUpdate(url)
          this.clonedHelper = response.data[this.model.store.mainField]
          let e = { id: this.model.id, data: data }
          this.$bus.$emit(`Clone-Grid-${this.model.id}`, e)
          this.$emit('onClone', e)
          this.requestGetData()
        })
        .catch(e => {
          this.isLoading = false
          this.requestErrorHandler(e, this.L10n('error-clone-item'))
        })
    },
    requestDeleteData (data, Callback) {
      this.isLoading = true
      return api
        .delete(this.checkJsTemplate(this.model.store.delete.url, data), { requestId: `requestDeleteData-${this._uid}-${data[this.model.store.mainField]}` })
        .then(response => {
          if (Callback) Callback(response)
          let e = { id: this.model.id, data: data }
          this.$bus.$emit(`Delete-Grid-${this.model.id}`, e)
          this.$emit('onDelete', e)
          this.requestGetData()
        })
        .catch(e => {
          this.isLoading = false
          this.requestErrorHandler(e, this.L10n('error-delete-item'))
        })
    },
    requestCreateData (data, Callback) {
      this.isLoading = true
      // delete ID
      let data_ = JSON.parse(JSON.stringify(data))
      delete data_[this.model.store.mainField]
      // request
      return api
        .post(this.checkJsTemplate(this.model.store.create.url, data_), data_)
        .then(response => {
          if (Callback) Callback(response)
          let e = { id: this.model.id, data: (response) ? response.data : '' }
          this.$bus.$emit(`Create-Grid-${this.model.id}`, e)
          this.$emit('onCreate', e)
          this.requestGetData()
        })
        .catch((e) => {
          this.isLoading = false
          this.requestErrorHandler(e, this.L10n('error-create-item'))
          if (Callback) Callback('Error')
        })
    },
    requestPatchData (Data, Callback) {
      this.isLoading = true
      return api({
        method: this.model.store.patch.method,
        url: this.checkJsTemplate(this.model.store.patch.url, Data),
        data: Data
      })
        .then(response => (Callback) ? Callback(response) : response)
        .then(response => {
          let e = { id: this.model.id, data: Data, response: response }
          this.$bus.$emit(`Patch-Grid-${this.model.id}`, e)
          this.$emit('onPath', e)
          this.requestGetData()
        })
        .catch(e => {
          this.isLoading = false
          this.requestErrorHandler(e, this.L10n('error-update-item'))
          if (Callback) Callback('Error')
        })
    },
    // HANDLERS
    // - ROUTER
    handleRoute () {
      let query = this.$route.query[this.model.id]
      if (
        !query ||
        (query && this.model.mode.isModalEditing) ||
        (query && query === 'new' && !this.model.mode.isModalNew)
      ) {
        this.isVisible = true
      } else {
        this.isVisible = false
      }
      if (this.isVisible && !this.isInit && this.model.isAutoLoad) {
        this.isInit = true
        this.requestGetDataDebounce()
      }
    },
    handleToggleSortable (Column) {
      if (Column.sortable.isAvailable) {
        if (this.model.sortable.field !== Column.field) {
          this.setSortable(Column.field, 'desc')
        } else if (this.model.sortable.order === 'desc') {
          this.setSortable(Column.field, 'asc')
        } else {
          this.setSortable()
        }
      }
    },
    // - GRID
    handlePagination () {
      this.requestGetData()
    },
    handlePageSize () {
      this.requestGetData()
    },
    handleCommand (Command) {
      switch (Command) {
        case 'addNew':
          this.addNew()
          break
        case 'refresh':
          this.refresh()
          break
        case 'excelCurrent':
          this.exportToExcel()
          break
        case 'excelAll':
          this.exportToExcel(true)
          break
        case 'jsonCurrent':
          this.exportToJson()
          break
        case 'jsonAll':
          this.exportToJson(true)
          break
      }
    },
    handleRowButton (Button, Row) {
      switch (Button.type) {
        case 'edit':
          this.handlerEdit(Row, Button)
          break
        case 'view':
          this.handlerView(Row, Button)
          break
        case 'clone':
          this.requestCloneData(Row)
          break
        case 'delete':
          this.handlerDelete(Row)
          break
        default:
          this.$emit('handleCustomButtonGrid', { grid: this.model.id, button: Button, row: Row })
          this.$bus.$emit('handleCustomButton-Grid', { grid: this.model.id, button: Button, row: Row })
      }
    },
    handlerEdit (row, Button) {
      let query = Object.assign({}, this.$route.query, { [this.model.id]: `edit[${row[this.model.store.mainField]}]` })
      let promise = Promise.resolve()
      if (this.model.mode.isModalPush) {
        promise = this.$router.push({ ...this.$router.currentRoute, query })
      } else {
        promise = this.$router.replace({ ...this.$router.currentRoute, query })
      }
      promise.then(() => {
        this.$bus.$emit('handlerEditRow-Grid', { grid: this.model.id, row: row })
      })
    },
    handlerView (row, Button) {
      let query = Object.assign({}, this.$route.query, { [this.model.id]: `preview[${row[this.model.store.mainField]}]` })
      let promise = Promise.resolve()
      if (this.model.mode.isModalPush) {
        promise = this.$router.push({ ...this.$router.currentRoute, query })
      } else {
        promise = this.$router.replace({ ...this.$router.currentRoute, query })
      }
      promise.then(() => {
        this.$bus.$emit('handlerPreviewRow-Grid', { grid: this.model.id, row: row })
      })
    },
    handlerDelete (data, callback) {
      this.$refs.dialogDelete.show(data, callback)
      this.$bus.$emit('handlerDeleteRow-Grid', { grid: this.model.id, row: data })
    },
    handleDeleteConfirm (data, callback) {
      this.requestDeleteData(data, callback)
    },
    handleDeleteCancel (data, Callback) {
      if (Callback) Callback('cancel')
    },
    handleEditorChange (RowData, Editor, Data, Callback) {
      if (Editor.customBehaviour) {
        return this.$emit('editor-custom-behaviour', RowData, Editor, Data, Callback)
      }
      if (this.model.store.patch.method.toLowerCase() === 'put') {
        this.requestPatchData(this.$_.reduce({ ...RowData, ...Data }, (ac, val, key) => {
          if (typeof val !== 'object') ac[key] = val
          return ac
        }, {}), Callback)
      } else {
        this.requestPatchData({ ...Data, [this.model.store.mainField]: RowData[this.model.store.mainField] }, Callback)
      }
    },
    handleColumn (Value) {
      if (Value.type === 'rank') {
        this.swapRank(Value)
      }
      if (Value.type === 'filter') {
        this.setToolbarFilter(Value.col.filterID, Value.col.filterValue(Value.row))
      }
    },
    handleFiltersInit (value) {
      this.handleRoute()
    },
    // - RESIZE // SCROLL
    handleResize (a1, a2) {
      const scrollWrap = this.$refs.scroll && this.$refs.scroll.$el
      const gridTable = this.$refs.gridTable
      if (scrollWrap && gridTable) {
        let x = scrollWrap.scrollLeft
        let y = scrollWrap.scrollTop
        this.scroll = {
          x: x,
          y: y,
          '-x': scrollWrap.clientWidth - gridTable.clientWidth + x
        }
      } else {
        this.scroll = { x: 0, y: 0, '-x': 0 }
      }
    },
    handleChangeScroll (newVal, oldVal) {
      this.$emit('changeScroll', newVal)
    },
    // - BUS
    handlerBusRefreshGrid (props) {
      if (props && props.id === this.model.id) this.requestGetData()
    },
    // METHODS
    /**
     * @desc Start row control action
     * @params 'type' => String
     * @params Row
    */
    actionRowButton ({ type, row }) {
      this.handleRowButton({ type }, row)
    },
    setLoading (value = false) {
      this.isLoading = !!value
    },
    loadData () {
      let waitDelay = 10000
      return waitValue(() => this.filtersInit, waitDelay)
        .then(() => {
          return this.requestGetDataDebounce()
        })
    },
    /**
     * @desc Set filters
     * @param filters { String }
     */
    setFilters (filters = '') {
      this.privateFilters = filters
    },
    setToolbarFilter (filterID, filterValue) {
      const filter = this.filters[filterID]

      if (!filter) {
        console.error(`Filter with id "${filterID}" is not found`)
        return
      }
      const eventID = `${this.model.id}-${filterID}`
      this.$bus.$emit(`on-set-filter-${eventID}`, filterValue)
    },
    addNew () {
      this.$router.replace({ query: Object.assign({}, this.$route.query, { [this.model.id]: 'new' }) })
      this.$bus.$emit('handlerAddNew-Grid', { grid: this.model.id })
      return this
    },
    refresh () {
      this.requestGetData()
      this.$bus.$emit('handlerRefresh-Grid', { grid: this.model.id })
      return this
    },
    resetData () {
      this.axios.cancel(`requestGetData-${this._uid}`)
      this.data = []
      this.clonedHelper = this.newHelper = null
      this.isLoading = false
      return this
    },
    getData () {
      return this.data
    },
    setData (Data, Cloned, New, Total) {
      this.data = Data.map(item => {
        return Object.assign({
          status_: {
            isNew: (New && New === item[this.model.store.mainField]),
            isClone: (Cloned && Cloned === item[this.model.store.mainField])
          }
        }, item)
      })
      // update total
      this.model.pagination.total = Total || Data.length
      this.clonedHelper = this.newHelper = null
      this.isLoading = false
      return this
    },
    setSortable (field = null, order = null) {
      this.model.sortable = { field: field, order: order }
      this.requestGetData()
    },
    exportToExcel (All = false) {
      this.isLoading = true;
      (async () => {
        const data = await new Promise((resolve) => {
          if (!All) {
            resolve(this.data)
          } else {
            let requestProps = this.createGetRequest(true)
            // send
            api
              .get(requestProps.url, {
                requestId: `requestExportData-${this._uid}`,
                params: requestProps.params
              })
              .then(response => {
                if (this.model.store.get.parser) {
                  resolve(this.model.store.get.parser(response.data.value))
                } else {
                  resolve(response.data.value)
                }
              })
              .catch(e => {
                throw new Error(e)
              })
          }
        })
        const name = this.checkJsTemplate(this.model.header.controls.export.excel[(All) ? 'all' : 'current'].titleFile) || this.model.header.title.value || ''
        const WbName = this.checkJsTemplate(this.model.header.controls.export.excel[(All) ? 'all' : 'current'].wbName) || name
        let columns = this.model.columns
          .filter(item => item.export.excel.isExport)
          .map(item => {
            return Object.assign({}, item.export.excel, { title: this.checkJsTemplate(item.export.excel.title) })
          })
        const excel = await ExcelExporter.export(name, columns, data, WbName)
        return excel
      })()
        .then(() => {
          this.isLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line
          console.error(e)
          this.isLoading = false
          this.requestErrorHandler(e, 'Failed export to Excel')
        })
    },
    exportToJson (All = false) {
      this.isLoading = true
      let columns = this.model.columns.filter(item => item.export.json.isExport).map(item => item.export.json);
      (async () => {
        const data = await new Promise((resolve) => {
          if (!All) {
            resolve(this.data)
          } else {
            let requestProps = this.createGetRequest(true)
            // send
            api
              .get(requestProps.url, {
                requestId: `requestExportData-${this._uid}`,
                params: requestProps.params
              })
              .then(response => {
                if (this.model.store.get.parser) {
                  resolve(this.model.store.get.parser(response.data.value))
                } else {
                  resolve(response.data.value)
                }
              })
              .catch(e => {
                throw new Error(e)
              })
          }
        })
        const json = await JsonExporter.export(this.checkJsTemplate(this.model.header.controls.export.json[(All) ? 'all' : 'current'].titleFile) || this.model.header.title.value || '', columns, data)
        return json
      })()
        .then(() => {
          this.isLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line
          console.error(e)
          this.isLoading = false
          this.requestErrorHandler(e, 'Failed export to Json')
        })
    },
    // HOOKS
    hookFilterRemoveSkip () {
      this.model.pagination.current = 1
    },
    // HELPERS
    resetHelpers () {
      this.scroll = { y: 0, x: 0 }
      return this
    },
    resetScroll () {
      const el = this.$refs.scroll && this.$refs.scroll.$el
      if (el) {
        el.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    setModePreview (bool) {
      this.model.mode.isPreviewMode = bool
    }
  }
}
</script>

<style lang="stylus">
  .grid-side {
    height 100%
    width 100%
    display flex
    flex-direction column
    align-items stretch
    .grid-wrapper {
      display flex
      flex-direction column
      align-items stretch
      margin 0
      flex 1
      overflow hidden
      position relative
      z-index 1
      border-top 1px solid #dddee1

      .scroll-area {
        width 100%
        height 100%
        position absolute
        left 0
        right 0
        top 0
        bottom 0
      }

      .ps__rail-x,
      .ps__rail-y{
        z-index: 10
      }

      > .el-loading-mask {
        left 1px
        right 1px
        top 1px
        bottom 1px
      }

      .grid-table {
        background #fff
        border 1px solid #dddee1
        border-top none
        flex 1
        overflow auto
        width 100%
        margin 0 0 .8rem
        min-height 25.5rem
        position relative

        table {
          border-collapse separate
          border-spacing 0
          /*table-layout fixed*/
          min-width 100%

          > thead {
            transform translateX(1)

            > tr {
              > th {
                border none
                border-bottom 2px solid #dddee1
                font-weight 400
                position relative
                background #fff
                will-change transform

                > span.title {
                  display block
                  color #a3aab0
                  text-transform capitalize
                  padding 1.5rem 2rem
                  pointer-events none
                  white-space nowrap
                  height 4.9rem

                  .icon-sort {
                    color #a3aab0
                    font-size 2rem
                    line-height 1.5rem
                    display inline-block
                    vertical-align middle
                    padding 0 0 0 .2rem
                  }

                  &.sortable {
                    pointer-events auto
                    cursor pointer
                  }
                }

                &.is-filtered > span.title {
                  padding-right 4rem
                }

                &:first-child {
                  > span.title {
                    padding-left calc(2rem + 3px)
                  }
                }

                &.is-sort {
                  border-bottom-color #a3aab0

                  > span.title {
                    color #a3aab0
                    font-weight 700

                    .icon-sort {
                      color #a3aab0
                    }
                  }
                }
              }
            }
          }

          > tbody {
            > tr {
              .el-form-item__content{
                line-height 3rem
              }
              .el-input__icon{
                line-height 3rem
              }
              &:nth-child(even) {
                td {
                  background #f9f9f9
                }
              }

              > td {
                border-top 1px solid #dddee1
                padding 0
                transition background .22s ease-in-out
                background #fff
                position relative
                z-index 2

                &.is-fixed {
                  z-index 6
                }

                &.is-edit {
                  transform translateX(1px)
                  z-index 7

                  &.is-fixed {
                    z-index 7
                  }
                }

                &:first-child {
                  border-left 3px solid transparent
                }
                .image {
                  margin 0 .5rem 0 0
                }

                .logo {
                  width 5rem
                  height 3.5rem
                }

                .table-column-content {
                  border-color transparent
                  background transparent
                }

                &.is-edit-available {
                  .table-column-content {
                    position relative
                    min-height 3rem
                    &:after {
                      opacity 0
                      transition opacity .22s ease-in-out
                      content '\f304'
                      font 900 1.6rem / 3.4rem 'Font Awesome 5 Free'
                      position absolute
                      top .2rem
                      right .2rem
                      background rgba(222, 222, 222, .5)
                      width 3rem
                      height 3.4rem
                      text-align center
                      padding 0 0 0 .2rem
                      color #fff
                      border-radius 0 4px 4px 0
                    }
                  }
                  &:hover {
                    .table-column-content {
                      border-color #dddee1

                      &:after {
                        opacity 1
                      }
                    }
                  }
                }

                &.ranked {
                  padding-left 3rem

                  .table-column-content {
                    position static

                    &:after {
                      top 50%
                      right 1rem
                      margin -1.5rem 0 0
                    }
                  }

                  .controls-ranked {
                    position absolute
                    top 0
                    left 0
                    bottom -1px
                    width 2.5rem
                    display flex
                    flex-direction column

                    button {
                      width 100%
                      height 50%
                      background #fff
                      padding 0
                      border-radius 0
                      border-top none

                      &:disabled {
                        opacity .3
                      }

                      &:hover {
                        background #ccc

                        i {
                          color #fff
                        }
                      }
                    }
                  }
                }
              }

              &:hover > td {
                background #f2f2f2

                &:first-child {
                  border-left-color #04ace7
                }
              }

              &.is-clone,
              &.is-new {
                > td {
                  animation blink-new-item 1s infinite
                }
              }

              &.is-edit {
                > td {
                  animation blink-edit-item 1s infinite
                }
              }
            }
          }

          /*will-change: transform*/

          .bottom-shadow {
            position absolute
            display block
            left 0
            right 0
            bottom calc(-1rem - 2px)
            height 1rem
            background linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0) 100%)
            opacity 0
            transition opacity .35s ease-in-out
            pointer-events none
          }

          &.is-scroll-y .bottom-shadow {
            opacity 1
          }

          .right-fixed-shadow:after {
            content ''
            position absolute
            top 0
            right -1rem
            bottom 0
            width 1rem
            background linear-gradient(to right, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0) 100%)
            opacity 0
            transition opacity .35s ease-in-out
            pointer-events none
          }

          .left-fixed-shadow:before {
            content ''
            position absolute
            top 0
            left -1rem
            bottom 0
            width 1rem
            background linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.15) 100%)
            opacity 0
            transition opacity .35s ease-in-out
            pointer-events none
          }

          th {
            &.right-fixed-shadow:after {
              background linear-gradient(to right, rgba(216, 216, 216, 1) 0%, rgba(255, 255, 255, 1) 100%)
            }

            &.left-fixed-shadow:before {
              background linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(216, 216, 216, 1) 100%)
            }
          }

          &.is-scroll-x {
            .right-fixed-shadow {
              &:after {
                opacity 1
              }

              &.left-fixed-shadow:before {
                opacity 1
              }
            }
          }

          &.is-scroll--x {
            .right-row-controls.left-fixed-shadow {
              &:before {
                opacity 1
              }
            }
          }
        }
      }

      .pagination-wrapper {
        display flex
        justify-content space-between
        align-items center
        padding 0

        .page-size {
          padding 0 3px

          > div {
            display flex
            align-items center
          }

          .size-title {
            padding-right 1rem
          }

          .total {
            padding-left 2rem
          }

          .el-select {
            width 10rem
          }
        }

        .pagination {
          margin 0
          padding 0 1.5rem 0 0
          line-height none
          text-transform capitalize

          li {
            padding 0 .3rem
            margin 0

            a {
              min-width 3.8rem
              line-height 3.8rem
              font-size 1.3rem
              padding 0 .3rem
              color #ccc
              text-decoration none
              background transparent
              display block
              box-sizing border-box
              border-radius 1.9rem
              text-align center

              &:hover {
                color #ccc
              }

              &:focus {
                box-shadow 0 0 0 0.2rem rgba(0, 123, 255, .25)
                color #000
              }

              &[disabled] {
                opacity .5
                pointer-events none
              }
            }

            &.active {
              a {
                color #000
                background #fff
                font-weight 700
              }
            }

            &.pagination-prev a {
              border-radius .4rem 1.9rem 1.9rem .4rem
            }

            &.pagination-next a {
              border-radius 1.9rem .4rem .4rem 1.9rem
            }
          }
        }
      }
    }

    &.is-preview-mode {
      .page-title .controls {
        .add-new {
          display none !important
        }
      }

      .grid-wrapper .grid-table table {
        > tbody > tr {
          > td.is-edit-available:hover {
            .table-column-content {
              border-color transparent

              &:after {
                opacity 0
              }
            }
          }

          &:hover > td {
            background #fff

            &:first-child {
              border-left-color transparent
            }
          }
        }
      }
    }

    &.inner-grid {
      height auto

      .grid-wrapper .grid-table {
        margin 0
      }

      .pagination-wrapper {
        margin-top .5rem
        margin-bottom 1rem

        .pagination li.active a {
          background #f2f2f2
        }
      }
    }
  }

  .grid-tools-wrap{
    display grid
    grid-template-columns 1fr auto
  }
  @keyframes blink-new-item {
    50% {
      background #fde9ea
    }
    100% {
      background #fff
    }
  }

  @keyframes blink-edit-item {
    50% {
      background #c6f4b3
    }
    100% {
      background #fff
    }
  }
  @media only screen and (max-width: 576px) {
    .grid-side{
      .grid-wrapper{
        margin 0
        .pagination-wrapper{
          padding 1rem
          justify-content center
        }
      }
    }
  }
</style>
